import { useTranslation } from "react-i18next";
import useFormHook from "@shared/hooks/useFormHook";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from "@fortawesome/free-solid-svg-icons";
import { useForm } from "react-hook-form";
import { useEffect, useState } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import LoadingButton from '@shared/components/LoadingButton';
import { notifyError } from '@config/tostifyConfig/tostifyConfig';
import { basisTerminationService } from '@modules/staff/basisForTermination/services/basisTermination';

export default function ReasonForLeavingForm({ onClose, onConfirm, editData }) {
    const { t } = useTranslation();
    const validationSchema = yup.object({
        code: yup.string().required(t('common.required')),
        longDescription: yup.string().required(t('common.required')),
    });
    const { modalRef } = useFormHook({}, onClose);
    const [fetchingData, setFetchingData] = useState(false);
    const { register, handleSubmit, setValue, reset, formState: { errors } } =
        useForm({
            resolver: yupResolver(validationSchema),
            defaultValues: {
                code: '',
                longDescription: '',
            }
        });

    // Fetch data from server if editing
    useEffect(() => {
        const fetchReasonLeavingData = async () => {
            if (editData && editData.id) {
                try {
                    setFetchingData(true);
                    const data = await basisTerminationService.getReasonLeavingById(editData.id);
                    
                    setValue('code', data.code || '');
                    setValue('longDescription', data.longDescription || '');
                    setValue('definition', data.definition || '');
                } catch (error) {
                    console.error('Error fetching reason leaving data:', error);
                    notifyError(error?.response?.data?.message || error?.message || t('common.errorMessage'));
                } finally {
                    setFetchingData(false);
                }
            } else {
                reset({
                    code: '',
                    longDescription: '',
                    definition: ''
                });
            }
        };

        fetchReasonLeavingData();
    }, [editData, setValue, reset, t]);

    const submitForm = async (data) => {
        try {
            const formData = {
                code: data.code,
                longDescription: data.longDescription,
                definition: data.definition
            };
            if (onConfirm) {
                await onConfirm(formData);
            }
        } catch (error) {
            const errorMessage = error.response?.data?.message || error.response?.data || t('common.errorMessage');
            notifyError(errorMessage);
        }
    }

    return (
        <div className="modal show" tabIndex="-1">
            <div className="modal-dialog modal-confirm" ref={modalRef} style={{ maxWidth: '500px', width: '90%' }}>
                <div className="modal-content">
                    <button type="button" className="close" onClick={onClose}>
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                    <div className="modal-body">
                        <h4 className="modal-title">
                            {editData ? t('common.edit') : t('common.add')}
                        </h4>
                    </div>
                    <form id="reasonForLeavingForm" className="region-form" onSubmit={handleSubmit(submitForm)}>
                        <div className="form-sections" style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                            <div className="form-section" style={{ width: '100%' }}>
                                <div className="form-group" style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', width: '100%' }}>
                                    <span>{t('staff.reasonForLeaving.table.headers.code')}<span className="required">*</span></span>
                                    <input type="text"
                                        maxLength={10}
                                        className={`form-control ${errors.code ? 'input-error' : ''}`}
                                        placeholder={errors.code ? errors.code.message : t('common.required')}
                                        style={{ width: '100%' }}
                                        disabled={!!editData}
                                        {...register('code')} />
                                </div>
                                <div className="form-group" style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', width: '100%', marginTop: '1rem' }}>
                                    <span>{t('staff.reasonForLeaving.table.headers.name')}<span className="required">*</span></span>
                                    <input type="text"
                                        maxLength={255}
                                        className={`form-control ${errors.longDescription ? 'input-error' : ''}`}
                                        placeholder={errors.longDescription ? errors.longDescription.message : t('common.required')}
                                        style={{ width: '100%' }}
                                        
                                        {...register('longDescription')} />
                                </div>
                                <div className="form-group" style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', width: '100%', marginTop: '1rem' }}>
                                    <span>{t('staff.reasonForLeaving.table.headers.definition')}</span> 
                                    <textarea rows={3} className={`form-control`}
                                        style={{ width: '100%' }}
                                        {...register('definition')} />
                                </div>
                            </div>
                        </div>
                    </form>
                    <div className="modal-footer">
                        <LoadingButton type="submit" form="reasonForLeavingForm" className="btn btn-primary" loading={fetchingData} disabled={fetchingData}>
                            {t('common.save')}
                        </LoadingButton>
                        <button type="button" className="btn btn-secondary" onClick={onClose} disabled={fetchingData}>
                            {t('common.cancel')}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

